import { signal } from '@angular/core';
import type { GameRenderer, GameResult } from '../../interfaces/game-type-plugin.interface';
import type {
  SportsGameConfig,
  SportsGameResult,
  ShotResult,
  ShotInput,
} from './sports.types';
import type { ShotEvaluation, StartingPosition } from './sport-definitions/sport-definition.interface';
import { SportDefinitionRegistry } from './sport-definitions/sport-definition.registry';
import { SportsSceneComponent } from './sports-scene.component';

/**
 * Sports game renderer.
 *
 * Orchestrates the multi-attempt game loop:
 *   1. Retrieve the sport definition from the registry.
 *   2. Compute starting positions for the configured attempt count.
 *   3. For each attempt:
 *      a. setPosition → scene moves ball to starting position.
 *      b. awaitNextShot → scene waits for player swipe → returns ShotInput.
 *      c. evaluateShot → sport definition scores the shot.
 *      d. animateShot → scene plays arc animation + result feedback.
 *   4. Resolve prize index from final score / Prize API.
 *   5. Return GameResult.
 *
 * Communication with the scene is entirely through scene.userData callbacks —
 * identical pattern to game-board-renderer.ts.
 */
export class SportsRenderer implements GameRenderer<SportsGameConfig> {
  private canvasStore: any | null = null;
  private arService: any | null = null;
  private configSignal = signal<SportsGameConfig | null>(null);

  // ─── GameRenderer interface ─────────────────────────────────────────────────

  setCanvasStore(store: any): void {
    this.canvasStore = store;
  }

  setArService(arService: any): void {
    this.arService = arService;
  }

  /**
   * Sports is 3D-only — the HTML container is not used.
   * The interface still requires this method; we store nothing.
   */
  initialize(_container: HTMLElement): void {
    // no-op: scene component owns all rendering
  }

  updateConfig(config: SportsGameConfig): void {
    this.configSignal.set(config);
    // Push config signal into scene.userData so the scene component reactively updates.
    const scene = this.getScene();
    if (scene) {
      scene.userData['configSignal'] = this.configSignal;
    }
  }

  getSceneComponent() {
    return SportsSceneComponent;
  }

  async play(options?: { getPrizeIndex?: () => Promise<number> }): Promise<GameResult | null> {
    const config = this.configSignal();
    if (!config) {
      console.error('[SportsRenderer] play() called before updateConfig()');
      return null;
    }

    const { settings, prizes } = config;
    const sportDef = SportDefinitionRegistry.get(settings.sportType);

    // Compute starting positions (may differ per attempt-count)
    const allPositions: StartingPosition[] = sportDef.getStartingPositions
      ? sportDef.getStartingPositions(settings.attempts)
      : sportDef.startingPositions.slice(0, settings.attempts);

    const shotResults: ShotResult[] = [];
    let totalScore = 0;

    for (let i = 0; i < settings.attempts; i++) {
      const position = allPositions[i % allPositions.length];

      // Ensure scene has set up its callbacks before proceeding
      await this.waitForSceneReady();

      // Move ball to starting position
      await this.callScene<void>('setPosition', position);

      // Update HUD: show attempt number and running score before each shot
      const scene = this.getScene();
      if (typeof scene?.userData?.['setHud'] === 'function') {
        scene.userData['setHud'](i + 1, settings.attempts, totalScore);
      }

      // Wait for player swipe
      const input = await this.callScene<ShotInput>('awaitNextShot');

      // Evaluate shot
      const evaluation: ShotEvaluation = sportDef.evaluateShot(
        input,
        settings.assistFactor,
        settings
      );

      // Trigger ball animation + result feedback
      await this.callScene<void>('animateShot', evaluation);

      shotResults.push({
        shotIndex: i,
        input,
        scored: evaluation.scored,
        pointsEarned: evaluation.pointsEarned,
        distance: evaluation.distance,
        zone: evaluation.zone,
        isWhiff: evaluation.isWhiff,
      });

      totalScore += evaluation.pointsEarned;
    }

    // ── Determine prize ──────────────────────────────────────────────────────
    let prizeIndex: number | null = null;

    if (settings.prizeDetermination === 'flat') {
      prizeIndex = settings.instantPrizeIndex ?? 0;
    } else if (
      settings.prizeDetermination === 'score-threshold' &&
      settings.scoreThresholds?.length
    ) {
      // Pick the highest tier the player's score reached
      const tiers = [...settings.scoreThresholds].sort(
        (a, b) => b.minScore - a.minScore
      );
      const matched = tiers.find((t) => totalScore >= t.minScore);
      prizeIndex = matched?.prizeIndex ?? null;
    } else if (
      (settings.prizeDetermination === 'leaderboard' ||
        settings.prizeDetermination === 'hybrid') &&
      options?.getPrizeIndex
    ) {
      prizeIndex = await options.getPrizeIndex();
    } else if ((config as SportsGameConfig & { _previewPrizes?: boolean })._previewPrizes && prizes.length > 0) {
      // Preview mode: pick a random prize so the UI shows a result
      prizeIndex = Math.floor(Math.random() * prizes.length);
    }

    const result: SportsGameResult = {
      totalScore,
      shots: shotResults,
      prizeIndex,
    };

    return {
      isWinner: prizeIndex !== null,
      prize: prizeIndex !== null && prizes[prizeIndex]
        ? { id: prizes[prizeIndex].id, name: prizes[prizeIndex].name, type: 'prize' }
        : undefined,
      metadata: result as unknown as Record<string, unknown>,
    };
  }

  destroy(): void {
    const scene = this.getScene();
    if (scene) {
      delete scene.userData['configSignal'];
      delete scene.userData['awaitNextShot'];
      delete scene.userData['animateShot'];
      delete scene.userData['setPosition'];
      delete scene.userData['resetGame'];
    }
    this.configSignal.set(null);
    this.canvasStore = null;
    this.arService = null;
  }

  async reset(): Promise<void> {
    const current = this.configSignal();
    if (!current) return;
    // Flip the _resetRequested flag so the scene component picks it up reactively
    this.configSignal.set({ ...current, _resetRequested: true });
    await new Promise<void>((resolve) => setTimeout(resolve, 50));
    this.configSignal.set({ ...current, _resetRequested: false });
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  private getScene(): any {
    // canvasStore is the raw state object passed from GameEngine (not the store itself)
    return this.canvasStore?.scene ?? null;
  }

  /**
   * Wait until the scene component has registered all userData callbacks.
   * Polls every 50 ms with a 5-second timeout.
   */
  private waitForSceneReady(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        const scene = this.getScene();
        if (typeof scene?.userData?.['awaitNextShot'] === 'function') {
          resolve();
          return;
        }
        if (Date.now() - start > 5000) {
          reject(new Error('[SportsRenderer] Scene did not become ready within 5 seconds'));
          return;
        }
        setTimeout(check, 50);
      };
      check();
    });
  }

  /**
   * Call a scene.userData function and await its result.
   * Throws a descriptive error if the callback is not registered.
   */
  private async callScene<T>(method: string, ...args: unknown[]): Promise<T> {
    const scene = this.getScene();
    const fn = scene?.userData?.[method];
    if (typeof fn !== 'function') {
      throw new Error(
        `[SportsRenderer] scene.userData['${method}'] is not registered. ` +
        `Ensure SportsSceneComponent has finished initializing.`
      );
    }
    return fn(...args) as T;
  }
}
